// src/hooks/useNotificationSystem.js
import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { collectionGroup, query, where, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { trackRead } from '../services/firestoreMonitor';
import toast from 'react-hot-toast';

// ─── Config ───────────────────────────────────────────────

const EARLY_ALERTS = [
  { minutes: 30, label: '30 minutos' },
  { minutes: 5, label: '5 minutos' },
];

const CHECK_INTERVAL = 30000;

const DEFAULT_SETTINGS = {
  enabled: true,
  workUpdates: { shared: true, assigned: true, access: true },
  communication: { mentions: true, comments: true, statusUpdates: true },
  summaries: { overdue: true },
  sound: true
};

// ─── Helpers ──────────────────────────────────────────────

const requestNotificationPermission = async () => {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const perm = await Notification.requestPermission();
  return perm === 'granted';
};

const showDesktopNotification = (title, body, type = 'default') => {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    // Use Service Worker notification for push support (works even when tab is inactive)
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.ready.then(reg => {
        reg.showNotification(title, {
          body,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: `gp-${type}-${Date.now()}`,
          vibrate: [200, 100, 200],
          requireInteraction: type === 'overdue',
          actions: [{ action: 'open', title: 'Abrir' }],
          data: { url: '/tareas', type }
        });
      });
    } else {
      const notif = new Notification(title, {
        body,
        icon: '/favicon.ico',
        tag: `gp-${Date.now()}`,
        requireInteraction: false
      });
      notif.onclick = () => { window.focus(); notif.close(); };
    }
  } catch (err) {
    console.error('[Notif] Error desktop notification:', err);
  }
};

const showToastNotification = (title, body, type = 'default') => {
  const icon = type === 'overdue' ? '🔴' : type === 'upcoming' ? '⚠️' : type === 'assigned' ? '📋' : '🔔';
  toast(
    (t) => (
      <div
        onClick={() => toast.dismiss(t.id)}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '8px' }}
      >
        <span style={{ fontSize: '16px', marginTop: '2px' }}>{icon}</span>
        <div>
          <div style={{ fontWeight: 600, fontSize: '13px' }}>{title}</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{body}</div>
        </div>
      </div>
    ),
    {
      duration: 6000,
      position: 'top-right',
      style: {
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderLeft: `4px solid ${type === 'overdue' ? '#ef4444' : type === 'upcoming' ? '#f59e0b' : type === 'assigned' ? '#3b82f6' : '#6b7280'}`,
        boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
        padding: '12px 16px',
        maxWidth: '380px',
      },
    }
  );
};

const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const playTone = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.12, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur);
    };
    playTone(880, 0, 0.15);
    playTone(1200, 0.18, 0.15);
  } catch {}
};

const parseDate = (dateField) => {
  if (!dateField) return null;
  if (dateField.toDate) return dateField.toDate();
  if (typeof dateField === 'object' && dateField.seconds) return new Date(dateField.seconds * 1000);
  const d = new Date(dateField);
  return isNaN(d.getTime()) ? null : d;
};

const formatTime = (date) => {
  return date.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
};

// ─── Hook ─────────────────────────────────────────────────

const useNotificationSystem = () => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();

  const notifiedRef = useRef(new Set());
  const notifiedAssignedRef = useRef(new Set());
  const isFirstLoadRef = useRef(true);
  const settingsRef = useRef(null);

  const notify = useCallback((type, title, body, extra = {}) => {
    const playSound = settingsRef.current?.sound !== false;

    // 1. Always show in-app toast (visible immediately)
    showToastNotification(title, body, type);

    // 2. Push/Desktop notification (if permitted)
    showDesktopNotification(title, body, type);

    // 3. Sound
    if (playSound) playNotificationSound();

    // 4. Persist to Firestore
    addNotification({ type, title, body, ...extra });
  }, [addNotification]);

  const checkTasks = useCallback((tasks) => {
    const settings = settingsRef.current;
    if (!settings?.summaries?.overdue) return;

    const now = new Date();

    tasks.forEach(task => {
      if (task.estado === 'finalizado') return;
      if (!task.fechaVencimiento) return;

      const dueDate = parseDate(task.fechaVencimiento);
      if (!dueDate) return;

      const diffMs = dueDate.getTime() - now.getTime();
      const diffMin = diffMs / 60000;

      // Upcoming alerts
      if (diffMs > 0) {
        for (const alert of EARLY_ALERTS) {
          const key = `${task.id}-${alert.minutes}`;
          if (notifiedRef.current.has(key)) continue;

          if (diffMin <= alert.minutes) {
            notifiedRef.current.add(key);
            notify(
              'upcoming',
              `Tarea por vencer en ${alert.label}`,
              `"${task.titulo}" - vence a las ${formatTime(dueDate)}`,
              { taskId: task.id, taskTitle: task.titulo }
            );
          }
        }
      }

      // Overdue
      if (diffMs < 0) {
        const overdueKey = `${task.id}-overdue`;
        if (notifiedRef.current.has(overdueKey)) return;

        notifiedRef.current.add(overdueKey);
        notify(
          'overdue',
          'Tarea Vencida',
          `"${task.titulo}" - venció ${dueDate.toLocaleDateString('es')} a las ${formatTime(dueDate)}`,
          { taskId: task.id, taskTitle: task.titulo }
        );
      }
    });
  }, [notify]);

  useEffect(() => {
    if (!user?.email) return;

    let unsubscribe = null;
    let intervalId = null;
    let tasksCache = {};

    const setup = async () => {
      // 1. Load notification settings (default to ENABLED)
      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        const settings = userSnap.data()?.notifications;

        // Use saved settings, or default to enabled
        const effectiveSettings = (settings && typeof settings.enabled !== 'undefined')
          ? settings
          : DEFAULT_SETTINGS;

        if (effectiveSettings.enabled === false) {
          console.log('[Notif] Notificaciones desactivadas por el usuario');
          return;
        }

        settingsRef.current = effectiveSettings;
      } catch (err) {
        console.error('[Notif] Error cargando config, usando defaults:', err);
        settingsRef.current = DEFAULT_SETTINGS;
      }

      // 2. Request browser permission (non-blocking: toasts work regardless)
      requestNotificationPermission().then(granted => {
        if (!granted) {
          console.log('[Notif] Sin permisos de escritorio, usando toasts in-app');
        }
      });

      console.log('[Notif] Sistema de notificaciones activo');

      // 3. Subscribe to assigned tasks (filtrado por workspace para reducir lecturas)
      try {
        const userWorkspaceId = user.workspaceId || user.uid;
        const tasksQuery = userWorkspaceId
          ? query(
              collectionGroup(db, 'items'),
              where('workspaceId', '==', userWorkspaceId),
              where('asignados', 'array-contains', user.email)
            )
          : query(
              collectionGroup(db, 'items'),
              where('asignados', 'array-contains', user.email)
            );

        unsubscribe = onSnapshot(tasksQuery, (snapshot) => {
          trackRead('items (notif)', snapshot.size, 'snapshot');
          snapshot.docChanges().forEach((change) => {
            const task = { id: change.doc.id, ...change.doc.data() };

            if (change.type === 'added' || change.type === 'modified') {
              tasksCache[task.id] = task;

              // Notify new assignment (skip first load)
              if (
                !isFirstLoadRef.current &&
                change.type === 'added' &&
                settingsRef.current?.workUpdates?.assigned &&
                !notifiedAssignedRef.current.has(task.id)
              ) {
                notifiedAssignedRef.current.add(task.id);
                notify(
                  'assigned',
                  'Nueva Tarea Asignada',
                  `Te han asignado: "${task.titulo}"`,
                  { taskId: task.id, taskTitle: task.titulo }
                );
              }
            }

            if (change.type === 'removed') {
              delete tasksCache[task.id];
            }
          });

          if (isFirstLoadRef.current) {
            isFirstLoadRef.current = false;
            const allTasks = Object.values(tasksCache);
            console.log(`[Notif] ${allTasks.length} tareas asignadas cargadas`);
            checkTasks(allTasks);
          }
        }, (error) => {
          console.error('[Notif] Error en listener:', error.message);
          if (error.message?.includes('index')) {
            toast.error('Se necesita un indice de Firestore para notificaciones. Revisa la consola.');
          }
        });
      } catch (err) {
        console.error('[Notif] Error configurando listener:', err);
      }

      // 4. Periodic check
      intervalId = setInterval(() => {
        checkTasks(Object.values(tasksCache));
      }, CHECK_INTERVAL);
    };

    isFirstLoadRef.current = true;
    notifiedRef.current = new Set();
    notifiedAssignedRef.current = new Set();

    setup();

    return () => {
      if (unsubscribe) unsubscribe();
      if (intervalId) clearInterval(intervalId);
    };
  }, [user?.uid, user?.email, user?.workspaceId]); // Re-subscribe on login/logout or workspace change
};

export default useNotificationSystem;
